import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert } from '@piggytechng/component-library';
import { httpClient } from '../api/http';
import {
  GenerateStatementPayload,
  StatementRow,
  StatementType,
  StatementWalletConfig,
  StatementWalletKey,
} from '../types';

export const STATEMENT_OPTIONS: { value: StatementType; label: string }[] = [
  { value: 'summary', label: 'Summary Statement' },
  { value: 'piggybank', label: 'Piggybank Statement' },
  { value: 'flexnaira', label: 'Flex Naira Statement' },
  { value: 'flexdollar', label: 'Flex Dollar Statement' },
  { value: 'safelock', label: 'Safelock Statement' },
];

export const STATEMENT_PAGE_SIZE = 20;

const first = <T>(...vals: T[]) => vals.find((v) => Array.isArray(v)) ?? [];

const WALLETS: Record<StatementWalletKey, StatementWalletConfig> = {
  piggybank: {
    url: (userId, page, limit) => `admin/piggybank/transactions/${userId}/${page}/${limit}`,
    pick: (data) => first(data?.results, data?.transactions, data),
    map: (raw, i) => ({
      id: String(raw.asw_id ?? raw.asw_ref ?? i),
      date: Number(raw.asw_date) || undefined,
      title: raw.asw_memo || '-----',
      amount: Number(raw.asw_value) || 0,
      balance: Number(raw.asw_balance) || 0,
      reference: raw.asw_ref,
    }),
  },
  flexnaira: {
    url: (userId, page, limit) => `admin/flexnaira/transactions/${userId}/${page}/${limit}`,
    pick: (data) => first(data?.transaction, data?.transactions, data),
    map: (raw, i) => ({
      id: String(raw.reference ?? i),
      date: Number(raw.date) || undefined,
      title: raw.memo || raw.title || '-----',
      amount: Number(raw.value) || 0,
      balance: Number(raw.balance) || 0,
      reference: raw.reference,
    }),
  },
  flexdollar: {
    url: (userId, page, limit) => `admin/flexdollar/transactions/${userId}/${page}/${limit}`,
    pick: (data) => first(data?.transactions, data?.transaction, data),
    isDollar: true,
    map: (raw, i) => ({
      id: String(raw.transactionId ?? i),
      date: Number(raw.date) || undefined,
      title: raw.memo || '-----',
      amount: Number(raw.amount) || 0,
      balance: Number(raw.balance) || 0,
      reference: raw.transactionId,
    }),
  },
  safelock: {
    // Safelock has no user-level transaction feed, so the statement lists the plans.
    url: (userId, page, limit) => `admin/safelock/pending/${userId}/?currency=ngn&page=${page}&limit=${limit}`,
    pick: (data) => first(data?.list, data?.results, data),
    isPlanList: true,
    map: (raw, i) => ({
      id: String(raw.asls_rand ?? raw.asls_id ?? i),
      date: Number(raw.asls_date) || undefined,
      title: raw.asls_title || 'Safelock',
      amount: Number(raw.asls_initial_amount) || 0,
      balance: Number(raw.asls_interest_amount) || 0,
      reference: raw.asls_rand,
      status: Number(raw.asls_status) === 0 ? 'Ongoing' : 'Matured',
    }),
  },
};

export const isDollarStatement = (type: StatementType) =>
  type !== 'summary' && !!WALLETS[type as StatementWalletKey]?.isDollar;

export const isPlanStatement = (type: StatementType) =>
  type !== 'summary' && !!WALLETS[type as StatementWalletKey]?.isPlanList;

const useAccountStatement = (type: StatementType) => {
  const params = useParams();
  const userIdIn = params.id as string;

  const [state, setState] = useState<{ list: StatementRow[]; page: number; hasMore: boolean }>({
    list: [],
    page: 0,
    hasMore: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = useCallback(
    async (page: number, append: boolean) => {
      if (!userIdIn || type === 'summary') return;
      const config = WALLETS[type as StatementWalletKey];
      if (!config) return;

      try {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);

        const res = await httpClient.get(config.url(userIdIn, page, STATEMENT_PAGE_SIZE));
        const incoming = config.pick(res.data?.data).map(config.map);

        setState((prev) => ({
          list: append ? [...prev.list, ...incoming] : incoming,
          page,
          hasMore: incoming.length === STATEMENT_PAGE_SIZE,
        }));
      } catch (error) {
        console.error(error);
        if (!append) setState({ list: [], page: 1, hasMore: false });
      } finally {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    },
    [userIdIn, type],
  );

  useEffect(() => {
    if (type === 'summary') {
      setState({ list: [], page: 0, hasMore: false });
      return;
    }
    fetchPage(1, false);
  }, [type, fetchPage]);

  const loadMore = () => {
    if (state.hasMore && !isLoadingMore) fetchPage(state.page + 1, true);
  };

  const [isGeneratingStatement, setIsGeneratingStatement] = useState(false);

  const generateAccountStatement = async (payload: GenerateStatementPayload, callback?: () => void) => {
    // The id is a path param, and comes from the form when there is no id on the route.
    const { userId, hideAddress, ...body } = payload;
    const targetUserId = userId?.trim() || userIdIn;

    if (!targetUserId) {
      Alert.error('A User ID is required to generate a statement');
      return;
    }

    const hideAddressQuery = hideAddress ? `?hideAddress=true` : '';

    try {
      setIsGeneratingStatement(true);
      const response: any = await httpClient.post(
        `admin/user/generate-account-statement/${targetUserId}${hideAddressQuery}`,
        body,
      );
      if (response.data.status) {
        Alert.success(response.data.message || 'Account statement generated successfully');
        callback?.();
        return response.data;
      }
      Alert.error(response.data.message || 'Failed to generate account statement');
    } catch (error: any) {
      Alert.error(error?.response?.data?.message || 'Failed to generate account statement');
    } finally {
      setIsGeneratingStatement(false);
    }
  };

  return {
    rows: state.list,
    hasMore: state.hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    generateAccountStatement,
    isGeneratingStatement,
  };
};

export default useAccountStatement;
